// ---------------------------------------------------------------------------
// Spacer Renderer
// ---------------------------------------------------------------------------
// Foundational primitive — publishes the editor `Spacer` block as a fixed
// vertical-height <div>. Skips the editor's preview-only "{N}px spacer"
// label because the published page should be silent whitespace, not text.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr } from "../escape.js";

interface SpacerProps {
  height?: string | number;
}

const SUPPORTED_HEIGHTS = new Set([
  "16",
  "32",
  "48",
  "64",
  "96",
  "128",
]);

const MAX_HEIGHT_PX = 256;

function resolveHeight(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return resolveHeight(String(Math.trunc(value)));
  }
  if (typeof value !== "string") return "48";
  const trimmed = value.trim();
  if (!trimmed) return "48";
  if (SUPPORTED_HEIGHTS.has(trimmed)) return trimmed;
  // Allow integer fallbacks within a sensible range.
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    if (n > 0 && n <= MAX_HEIGHT_PX) return trimmed;
  }
  return "48";
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as SpacerProps;
  const height = resolveHeight(p.height);

  return `<div class="ease-section ease-spacer" aria-hidden="true" style="height: ${attr(height)}px;"></div>`;
}

registerRenderer("spacer", { render });
